/**
 * TerraMatrix Universal Data Platform - Data API Service
 *
 * Sprint 5: Frontend - Data Page
 * Created: November 2025
 * Updated: December 2025 - Phase 6 NL Query Integration
 *
 * Connects to the data-service for natural language queries,
 * data sources, and query operations.
 */

import axios, { type AxiosInstance } from 'axios';
import { applyDataServiceOrganizationHeaders, type DataServiceListingScope } from './data-service-client';
import { getCurrentAuthTokens } from './auth-session';
import type { QueryTemplate } from './queryTemplateApi';
import type { CompositeQueryDefinition } from '@/types/chartDataSource';

// Data service base URL (runs on port 8002)
const DATA_SERVICE_BASE_URL = import.meta.env.VITE_DATA_SERVICE_URL || '/data-api';

// Create axios instance for data service
const createDataServiceClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: DATA_SERVICE_BASE_URL,
    timeout: 60000, // 60 seconds for query execution
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor for authentication
  client.interceptors.request.use(
    async (config) => {
      const tokens = getCurrentAuthTokens();

      // Add auth token if available
      if (tokens?.accessToken) {
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;
      }

      return applyDataServiceOrganizationHeaders(config, { allowUnscopedHealthCheck: true });
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor for error handling
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      // Data Service returns error code in 'error' field, not 'code'
      const apiError = {
        code: error.response?.data?.error || error.response?.data?.code || 'UNKNOWN_ERROR',
        message: error.response?.data?.message || error.message || 'An unexpected error occurred',
        details: error.response?.data?.details,
        status: error.response?.status,
      };
      console.error('[dataApi] Request failed:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        code: apiError.code,
        message: apiError.message,
      });
      return Promise.reject(apiError);
    }
  );

  return client;
};

const dataServiceClient = createDataServiceClient();

function getDataOwnerRequestConfig(
  options?: DataRecordMutationOptions,
): { headers: { 'X-Organization-Id': string } } | undefined {
  const dataOwnerOrganizationId = options?.dataOwnerOrganizationId?.trim();
  if (!dataOwnerOrganizationId) {
    return undefined;
  }

  return {
    headers: {
      'X-Organization-Id': dataOwnerOrganizationId,
    },
  };
}

// Agent query client — routes NL queries through the agent system (main backend → agent-service)
// This enables knowledge-enriched data queries via the MQTT agent network
const MAIN_API_URL = (import.meta.env.VITE_API_BASE_URL || '/backend-api/api/v1').replace(/\/api\/v1$/, '');
const agentQueryClient = axios.create({
  baseURL: MAIN_API_URL,
  timeout: 120000, // NL queries route through agent-proxy → agent-service (90s MQTT) + overhead
  headers: { 'Content-Type': 'application/json' },
});

agentQueryClient.interceptors.request.use(
  async (config) => {
    const tokens = getCurrentAuthTokens();

    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }

    return applyDataServiceOrganizationHeaders(config, { allowUnscopedHealthCheck: false });
  },
  (error) => Promise.reject(error)
);

// ============================================================================
// Types
// ============================================================================

/**
 * Template query structure (matches backend ITemplateQuery)
 * Used for saving NL-generated queries as reusable templates
 */
export interface TemplateQuery {
  schemaId: string;
  groupId: string;
  measures: Array<{
    field: string;
    aggregation: string;
    alias?: string;
  }>;
  groupBy?: Array<string | { field: string; granularity?: string }>;
  filters?: Record<string, unknown>;
  timeRange?: {
    field: string;
    preset?: string;
    from?: string;
    to?: string;
  };
  orderBy?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  limit?: number;
}

/**
 * Template variable definition
 */
export interface TemplateVariable {
  type: 'system' | 'timePreset' | 'dateRange' | 'enum' | 'string';
  default?: unknown;
  description?: string;
  options?: string[];
}

export type ClarificationCategory =
  | 'source_selection'
  | 'metric_selection'
  | 'time_range'
  | 'aggregation'
  | 'filter'
  | 'intent'
  | 'other';

export interface ClarificationOption {
  id: string;
  label: string;
  value: string;
  description?: string;
  confidence?: number;
  reasoning?: string;
}

export interface ClarificationResumeContext {
  originalPrompt: string;
  clarificationType: ClarificationCategory;
  sourceHint?: string;
  requestedCategory?: RequestedTemplateIntentCategory;
  availableSourceIds?: string[];
  depth?: number;
  consumedAmbiguityIds?: string[];
}

export interface ClarificationMetadata {
  question: string;
  category: ClarificationCategory;
  type: ClarificationCategory;
  options: ClarificationOption[];
  allowsFreeText: boolean;
  originalPrompt: string;
  ambiguityReason: string;
  resumeContext: ClarificationResumeContext;
}

export interface ClarificationAnswer {
  value: string;
  label?: string;
  optionId?: string;
  isFreeText?: boolean;
}

export interface ClarificationResponsePayload {
  originalPrompt: string;
  clarification: ClarificationMetadata;
  answer: ClarificationAnswer;
  depth: number;
  consumedAmbiguityIds: string[];
}

export type TemplateIntentCategory =
  | 'single_metric_over_time'
  | 'multi_metric_same_source'
  | 'multi_source_combined'
  | 'period_comparison'
  | 'derived_metric'
  | 'needs_clarification'
  | 'needs_review';

export type RequestedTemplateIntentCategory = Exclude<TemplateIntentCategory, 'needs_clarification' | 'needs_review'>;

export interface TemplateIntentMetadata {
  category: TemplateIntentCategory;
  requestedCategory?: RequestedTemplateIntentCategory;
  needsReview?: boolean;
  reviewReason?: string;
  needsClarification?: boolean;
  clarificationReason?: string;
  clarification?: ClarificationMetadata;
}

/**
 * LLM prompt/response capture for debugging
 */
export interface LLMPromptCapture {
  systemPrompt: string;
  userMessage: string;
  rawResponse: string;
}

/**
 * All LLM prompts and responses from the NL query pipeline
 */
export interface NLQueryPrompts {
  schemaIdentification?: LLMPromptCapture;
  intentAnalysis?: LLMPromptCapture;
  fieldIdentification?: LLMPromptCapture;
  queryGeneration?: LLMPromptCapture;
  validation?: LLMPromptCapture;
}

/**
 * Accessible schema info for debugging
 */
export interface AccessibleSchema {
  id: string;
  name: string;
  displayName: string;
  description: string;
  tags: string[];
  groupId: string;
  groupName: string;
  fields?: Array<
    | string
    | {
        name: string;
        path?: string;
        type?: string;
        dataType?: string;
        technicalType?: string;
        description?: string;
      }
  >;
}

/**
 * Schema context sent to LLM for debugging
 */
export interface SchemaContextDebug {
  accessibleSchemas: AccessibleSchema[];
  schemaIndex: string;
}

/**
 * Debug info for NL query pipeline
 */
export interface NLQueryDebugInfo {
  intent: unknown;
  matchedSources: Array<{
    schemaId: string;
    schemaName: string;
    groupId: string;
    confidence: number;
    reason: string;
  }>;
  selectedFields?: {
    fields: Array<{
      name: string;
      path: string;
      usage: string;
      aggregation?: string;
      alias?: string;
    }>;
    reasoning: string;
  };
  generatedQuery?: unknown;
  validationResult?: {
    valid: boolean;
    issues: string[];
    securityConcerns: string[];
    suggestions?: string[];
  };
  executionTimeMs: number;
  stepTimings?: {
    accessControl: number;
    schemaIdentification: number;
    fieldIdentification: number;
    queryGeneration: number;
    validation: number;
    execution: number;
  };
  /** LLM prompts and responses for each step */
  prompts?: NLQueryPrompts;
  /** Schema context sent to LLM */
  schemaContext?: SchemaContextDebug;
}

export interface QueryResponse {
  success: boolean;
  answer?: string;
  data?: unknown[];
  visualization?: {
    type: 'bar' | 'line' | 'pie' | 'table' | 'metric';
    config: Record<string, unknown>;
  };
  source?: { id: string; name: string };
  followUpSuggestions?: string[];
  error?: string;
  errorCode?: string;
  suggestion?: string;
  needsSourceSelection?: boolean;
  availableSources?: Array<{
    id: string;
    name: string;
    confidence: number;
    reasoning: string;
  }>;
  clarification?: ClarificationMetadata;
  debug?: NLQueryDebugInfo;
  /** Template query structure (for saving as reusable template) */
  templateQuery?: TemplateQuery;
  /** Extracted variables (for saving as reusable template) */
  templateVariables?: Record<string, TemplateVariable>;
  /** Optional template intent/result taxonomy for AI-generated templates */
  templateIntent?: TemplateIntentMetadata;
}

export interface CompositeGenerationResponse {
  compositeQueryDefinition?: CompositeQueryDefinition;
  singleSourceResult?: QueryResponse;
  explanation: string;
  needsReview?: boolean;
  reviewReason?: string;
  needsClarification?: boolean;
  clarificationReason?: string;
  clarification?: ClarificationMetadata;
}

export interface QueryRequestOptions {
  responseMode?: 'default' | 'template-create';
  clarificationResponse?: ClarificationResponsePayload;
}

export interface DataRecordMutationOptions {
  dataOwnerOrganizationId?: string;
}

export interface BulkDataRecordInput {
  data: Record<string, unknown>;
  metadata?: {
    source?: string;
    tags?: string[];
  };
}

export interface ExplainResponse {
  intent: {
    type: string;
    entities: string[];
    metrics: string[];
    aggregation?: string;
    timeRange?: unknown;
    filters: unknown[];
    confidence: number;
  };
  matchedSources: Array<{
    dataSourceId: string;
    name: string;
    type: string;
    score: number;
    matchReasons: string[];
  }>;
  suggestedQuery?: {
    queryType: string;
    query: unknown;
    explanation: string;
    confidence: number;
  };
}

export interface DataSource {
  dataSourceId: string;
  name: string;
  type: 'mongodb' | 'mysql' | 'postgresql';
  status: 'pending' | 'active' | 'error' | 'disabled';
  metadata?: {
    description?: string;
    tags?: string[];
    domain?: string;
    answersQuestionsAbout?: string[];
  };
  schema?: {
    tables: Array<{
      name: string;
      rowCount?: number;
      fields: Array<{
        name: string;
        technicalType: string;
        description?: string;
      }>;
    }>;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface OnboardingSession {
  sessionId: string;
  dataSourceId: string;
  phase: 'greeting' | 'data_purpose' | 'key_metrics' | 'relationships' | 'confirmation' | 'completed';
  conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }>;
  extractedInfo: Record<string, unknown>;
  isComplete: boolean;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Check if the data service is reachable.
 * Returns true if healthy, false otherwise.
 */
export async function checkDataServiceHealth(): Promise<boolean> {
  try {
    await dataServiceClient.get('/health', { timeout: 3000 });
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if the workflow service is reachable.
 * Returns true if healthy, false otherwise.
 */
export async function checkWorkflowServiceHealth(): Promise<boolean> {
  try {
    const client = axios.create({ baseURL: '/workflow-api' });
    await client.get('/health', { timeout: 3000 });
    return true;
  } catch {
    return false;
  }
}

export const dataApi = {
  // =========================================================================
  // Query API
  // =========================================================================

  /**
   * Execute a natural language query against connected data sources
   */
  query: async (
    query: string,
    sourceHint?: string,
    includeDebug = false,
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string; schemaId?: string }>,
    options?: QueryRequestOptions
  ): Promise<QueryResponse> => {
    const response = await agentQueryClient.post('/api/v1/agent/data/query', {
      query,
      sourceHint,
      includeDebug,
      ...(options?.responseMode && { responseMode: options.responseMode }),
      ...(options?.clarificationResponse && { clarificationResponse: options.clarificationResponse }),
      ...(conversationHistory && conversationHistory.length > 0 && {
        conversationHistory: conversationHistory.slice(-10),
      }),
    });
    return response.data;
  },

  /**
   * Save a natural language query as a reusable template (legacy)
   */
  saveAsTemplate: async (params: {
    query: string;
    name?: string;
    description?: string;
    schemaId: string;
    groupId: string;
  }): Promise<{ templateId: string; template: QueryTemplate }> => {
    const response = await dataServiceClient.post('/api/v1/query/templates/save', params);
    return response.data.data;
  },

  /**
   * Save an NL-generated query as a reusable template
   * Uses the new ITemplateQuery format with variable definitions
   */
  saveNLQueryAsTemplate: async (params: {
    name: string;
    description?: string;
    query: TemplateQuery;
    variables: Record<string, TemplateVariable>;
  }): Promise<{ templateId: string; template: QueryTemplate }> => {
    const response = await dataServiceClient.post('/api/nl/save', params);
    return response.data.data || response.data;
  },

  /**
   * Explain how a query would be processed without executing it
   */
  explain: async (query: string): Promise<ExplainResponse> => {
    const response = await dataServiceClient.post('/api/nl/explain', { query });
    return response.data;
  },

  /**
   * Get query suggestions for the user
   */
  getSuggestions: async (): Promise<{ suggestions: string[] }> => {
    const response = await dataServiceClient.get('/api/nl/suggestions');
    return response.data;
  },

  /**
   * Get available NL query data sources for the organization
   */
  getNLSources: async (options?: { scope?: DataServiceListingScope }): Promise<{
    sources: Array<{
      id: string;
      name: string;
      description?: string;
      tags?: string[];
      group?: string;
      groupId?: string;
      fields?: string[];
    }>;
    count: number;
  }> => {
    const response = await dataServiceClient.get('/api/nl/sources', {
      params: { scope: options?.scope ?? 'current' },
    });
    return response.data.data || response.data;
  },

  /**
   * Generate a CompositeQueryDefinition from a natural language prompt.
   * Calls POST /api/nl/generate-composite on the data-service.
   * Returns the definition for user review before applying to a component.
   */
  generateCompositeQuery: async (
    prompt: string,
    clarificationResponse?: ClarificationResponsePayload
  ): Promise<CompositeGenerationResponse> => {
    const response = await dataServiceClient.post(
      '/api/nl/generate-composite',
      {
        prompt,
        ...(clarificationResponse && { clarificationResponse }),
      },
      { timeout: 120000 }
    );
    return response.data.data || response.data;
  },

  /**
   * Get NL query service status
   */
  getNLStatus: async (): Promise<{ available: boolean; message: string }> => {
    const response = await dataServiceClient.get('/api/nl/status');
    return response.data.data || response.data;
  },

  /**
   * Get full schema details for an accessible schema (via group membership).
   * Used by FormBuilderAgent for form generation.
   */
  getAccessibleSchema: async (schemaId: string): Promise<{
    id: string;
    name: string;
    displayName: string;
    description: string;
    status: string;
    visibility: string;
    dataType: string;
    fields: Array<{
      id: string;
      name: string;
      type: string;
      required: boolean;
      description?: string;
      constraints?: Record<string, unknown>;
    }>;
    tags: string[];
    groupId: string;
    groupName: string;
  }> => {
    const response = await dataServiceClient.get(`/api/nl/schema/${schemaId}`);
    return response.data.data || response.data;
  },

  // =========================================================================
  // Data Sources API
  // =========================================================================

  /**
   * Get all data sources for the organization
   * Uses NL query sources endpoint which returns accessible schemas
   */
  getSources: async (): Promise<DataSource[]> => {
    const response = await dataServiceClient.get('/api/nl/sources');
    // Map the NL sources response to DataSource format
    const sources = response.data.data?.sources || response.data.sources || [];
    return sources.map((s: { id: string; name: string; description?: string; tags?: string[]; group?: string; groupId?: string }) => ({
      id: s.id,
      name: s.name,
      type: 'mongodb' as const, // Default type for schema-based sources
      status: 'connected' as const,
      connectionConfig: {},
      metadata: {
        description: s.description,
        tags: s.tags,
        group: s.group,
        groupId: s.groupId,
      },
    }));
  },

  /**
   * Get a single data source by ID
   */
  getSource: async (id: string): Promise<DataSource> => {
    const response = await dataServiceClient.get(`/api/analytics/data-sources/${id}`);
    return response.data.data || response.data;
  },

  /**
   * Create a new data source
   */
  createSource: async (source: {
    name: string;
    type: DataSource['type'];
    connectionConfig: Record<string, unknown>;
    metadata?: DataSource['metadata'];
  }): Promise<DataSource> => {
    const response = await dataServiceClient.post('/api/analytics/data-sources', source);
    return response.data.data || response.data;
  },

  /**
   * Update a data source
   */
  updateSource: async (id: string, updates: Partial<DataSource>): Promise<DataSource> => {
    const response = await dataServiceClient.put(`/api/analytics/data-sources/${id}`, updates);
    return response.data.data || response.data;
  },

  /**
   * Delete a data source
   */
  deleteSource: async (id: string): Promise<void> => {
    await dataServiceClient.delete(`/api/analytics/data-sources/${id}`);
  },

  /**
   * Test connection to a data source
   */
  testConnection: async (id: string): Promise<{ success: boolean; latencyMs: number; error?: string }> => {
    const response = await dataServiceClient.post(`/api/analytics/test-connection`, { dataSourceId: id });
    return response.data.data || response.data;
  },

  /**
   * Discover schema from a data source
   */
  discoverSchema: async (id: string): Promise<{ schema: DataSource['schema'] }> => {
    const response = await dataServiceClient.post(`/api/analytics/custom-discover-schema`, { dataSourceId: id });
    return response.data.data || response.data;
  },

  /**
   * Get the current schema of a data source
   */
  getSchema: async (id: string): Promise<DataSource['schema']> => {
    const response = await dataServiceClient.get(`/api/analytics/data-sources/${id}/schema`);
    return response.data.data || response.data;
  },

  /**
   * Get sample data from a table
   */
  getSampleData: async (id: string, tableName: string, limit = 10): Promise<{ data: unknown[] }> => {
    const response = await dataServiceClient.get(`/api/analytics/data-sources/${id}/sample/${tableName}`, {
      params: { limit },
    });
    return response.data.data || response.data;
  },

  // =========================================================================
  // Onboarding API
  // =========================================================================

  /**
   * Start an onboarding session for a data source
   */
  startOnboarding: async (dataSourceId: string): Promise<{ sessionId: string; message: string }> => {
    const response = await dataServiceClient.post('/api/onboarding/start', { dataSourceId });
    return response.data.data || response.data;
  },

  /**
   * Send a message in an onboarding conversation
   */
  sendOnboardingMessage: async (
    sessionId: string,
    message: string
  ): Promise<{ response: string; phase: string; isComplete: boolean }> => {
    const response = await dataServiceClient.post(`/api/onboarding/${sessionId}/message`, { message });
    return response.data.data || response.data;
  },

  /**
   * Get the current state of an onboarding session
   */
  getOnboardingSession: async (sessionId: string): Promise<OnboardingSession> => {
    const response = await dataServiceClient.get(`/api/onboarding/${sessionId}`);
    return response.data.data || response.data;
  },

  /**
   * List all onboarding sessions
   */
  listOnboardingSessions: async (): Promise<OnboardingSession[]> => {
    const response = await dataServiceClient.get('/api/onboarding');
    return response.data.data || response.data;
  },

  /**
   * Complete an onboarding session
   */
  completeOnboarding: async (sessionId: string): Promise<void> => {
    await dataServiceClient.post(`/api/onboarding/${sessionId}/complete`);
  },

  /**
   * Abandon an onboarding session
   */
  abandonOnboarding: async (sessionId: string): Promise<void> => {
    await dataServiceClient.post(`/api/onboarding/${sessionId}/abandon`);
  },

  /**
   * Analyze fields in a table
   */
  analyzeFields: async (
    dataSourceId: string,
    tableName: string
  ): Promise<{ fields: Array<{ name: string; analysis: unknown }> }> => {
    const response = await dataServiceClient.get(`/api/onboarding/fields/${dataSourceId}/${tableName}`);
    return response.data.data || response.data;
  },

  // =========================================================================
  // Data Record API (Direct Form Submission)
  // =========================================================================

  /**
   * Create a data record in a schema group.
   * Used for direct form submission (bypassing workflow engine).
   *
   * @param groupId - The schema group ID
   * @param schemaId - The schema ID for validation
   * @param data - The record data (form values)
   * @param metadata - Optional metadata (source, tags)
   */
  createRecord: async (
    groupId: string,
    schemaId: string,
    data: Record<string, unknown>,
    metadata?: { source?: string; tags?: string[] },
    options?: DataRecordMutationOptions
  ): Promise<{ insertedId: string; schemaVersion: number }> => {
    const response = await dataServiceClient.post(
      `/api/groups/${groupId}/data`,
      {
        schemaId,
        data,
        metadata: {
          source: metadata?.source || 'form',
          tags: metadata?.tags || ['form-submission'],
        },
      },
      getDataOwnerRequestConfig(options),
    );
    return response.data.data || response.data;
  },

  /**
   * Create multiple data records in a schema group.
   * Used by CSV/data import flows that need to seed a newly created dataset.
   */
  createRecords: async (
    groupId: string,
    schemaId: string,
    records: BulkDataRecordInput[],
    options?: DataRecordMutationOptions,
  ): Promise<{
    insertedIds: string[];
    insertedCount: number;
    schemaVersion: number;
  }> => {
    const response = await dataServiceClient.post(
      `/api/groups/${groupId}/data/bulk`,
      {
        schemaId,
        records,
      },
      getDataOwnerRequestConfig(options),
    );
    return response.data.data || response.data;
  },

  /**
   * Update a data record's fields in a schema group.
   * Used by PM components (Kanban, Gantt, Calendar) for writeback.
   *
   * @param groupId - The schema group ID
   * @param schemaId - The schema ID for validation
   * @param recordId - The record ID to update
   * @param data - Partial field changes to apply
   */
  updateRecord: async (
    groupId: string,
    schemaId: string,
    recordId: string,
    data: Record<string, unknown>,
    options?: DataRecordMutationOptions
  ): Promise<{ modifiedCount: number; record: Record<string, unknown> }> => {
    const response = await dataServiceClient.put(
      `/api/groups/${groupId}/data/${recordId}`,
      { schemaId, data },
      getDataOwnerRequestConfig(options),
    );
    return response.data.data || response.data;
  },

  /**
   * Delete a data record from a schema group.
   *
   * @param groupId - The schema group ID
   * @param schemaId - The schema ID for collection lookup
   * @param recordId - The record ID to delete
   */
  deleteRecord: async (
    groupId: string,
    schemaId: string,
    recordId: string,
    options?: DataRecordMutationOptions,
  ): Promise<{ deletedCount: number }> => {
    const response = await dataServiceClient.delete(
      `/api/groups/${groupId}/data/${recordId}`,
      {
        ...getDataOwnerRequestConfig(options),
        params: { schemaId },
      },
    );
    return response.data.data || response.data;
  },

  /**
   * Query data records from a schema group.
   * Used by Table/Chart components to fetch schema data at runtime.
   */
  queryRecords: async (
    groupId: string,
    schemaId: string,
    options?: { limit?: number; page?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }
  ): Promise<{ records: Record<string, unknown>[]; total: number }> => {
    const response = await dataServiceClient.get(`/api/groups/${groupId}/data`, {
      params: { schemaId, ...options },
    });
    return {
      records: response.data?.data || [],
      total: response.data?.pagination?.total || 0,
    };
  },

  /**
   * Compute derived metrics from multiple schemas (deterministic, no LLM).
   * Used by KPIs that need cross-schema data (e.g. percent to goal, days to goal).
   */
  computeMetrics: async (params: {
    groupId: string;
    metrics: string[];
    schemas: { entries?: string; goals?: string };
    config?: {
      currentValueField?: string;
      goalValueField?: string;
      startValueField?: string;
      dateField?: string;
      targetDateField?: string;
    };
  }): Promise<Record<string, number | null>> => {
    const response = await dataServiceClient.post('/api/computed-metrics', params);
    return response.data?.data || response.data || {};
  },
};

export default dataApi;
